import { css } from '@emotion/react';
import { alpha } from '@mui/material/styles';

// The gap between day tiles / time pills on a page. SlotGrid's item-width maths
// (itemBasis) subtracts this per gap, so both must use the one value.
export const ITEM_GAP = 8;

const makeSlotPickerStyles = ({ theme }) => {
  // Secondary text (card weekday/month, the view-all link) reads one step quieter
  // than the primary text. Deriving it from the primary colour keeps it themed
  // and always lighter, where some themes set the secondary token to the same
  // near-black as the primary.
  const mutedText = alpha(theme.mvf.palette.text.primaryTextColor, 0.6);
  return {
    container: css({ maxWidth: 640, margin: '0 auto', textAlign: 'left' }),
    confirmationContainer: css({
      maxWidth: 640,
      margin: '0 auto',
      textAlign: 'center',
    }),
    // The confirmation tick uses a fixed, theme-independent success teal (and a
    // translucent version of it for the circle) per the provided design.
    confirmationTick: css({
      width: 80,
      height: 80,
      margin: '0 auto 15px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      // Hardcoded confirmation-tick green (not a theme token) — stakeholder
      // greenlit for the MVP.
      backgroundColor: '#1d917a26',
      color: '#1d917a',
    }),
    backButtonContainer: css({ textAlign: 'left' }),
    heading: css({
      marginTop: 10,
      '& h6': { fontWeight: 900, fontSize: 30, lineHeight: 1 },
    }),
    subheading: css({ margin: '10px 0 15px 0' }),
    // Extra breathing room between "Pick a date & time" and the first card.
    slotPickerHeadingGap: css({ marginBottom: '2rem' }),
    // The cards scroll (invisible scrollbar) within a max-height computed from
    // the form's available space (see useOlbScrollMaxHeight), so the heading and
    // the sticky Continue footer stay visible.
    cardScrollArea: css({
      minHeight: 0,
      overflowY: 'auto',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }),
    storeNameBold: css({ fontWeight: 600 }),
    sectionLabel: css({
      textAlign: 'left',
      '& p': {
        textAlign: 'left',
        fontWeight: theme.mvf.typography.weight.thick,
      },
    }),
    // The times heading row: date on the left, the time paging arrows on the
    // right, mirroring the day row (range left, arrows right).
    timesHead: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      margin: '15px 0 8px 0',
    }),
    pillRow: css({
      display: 'flex',
      flexWrap: 'wrap',
      gap: ITEM_GAP,
      justifyContent: 'flex-start',
    }),
    pill: css({
      textTransform: 'none',
      borderRadius: '20px',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      padding: '6px 16px',
      color: theme.mvf.palette.text.primaryTextColor,
    }),
    // A time pill inside the paged row: it takes an equal share of the page
    // (flex-basis set inline), so minWidth is cleared (MUI's Button defaults to
    // 64px) and the text centres. Tighter side padding so HH:MM fits at phone
    // widths where a page is narrow.
    timePill: css({
      textTransform: 'none',
      minWidth: 0,
      borderRadius: '20px',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      padding: '6px 8px',
      color: theme.mvf.palette.text.primaryTextColor,
      whiteSpace: 'nowrap',
    }),
    pillActive: css({
      backgroundColor: theme.mvf.palette.component.answerSelectedColor,
      borderColor: theme.mvf.palette.component.answerSelectedColor,
      color: theme.mvf.palette.background.widgetBackgroundColor,
      '&:hover': {
        backgroundColor: theme.mvf.palette.component.answerSelectedColor,
      },
    }),
    // Each tile takes an equal share of the page (flex-basis set inline from the
    // page size), so the tiles and the peeking card are the same width.
    dayTile: css({
      textTransform: 'none',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: 2,
      lineHeight: 1.1,
      borderRadius: '10px',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      padding: '14px 10px 15px',
      color: theme.mvf.palette.text.primaryTextColor,
      [theme.breakpoints.up('tabletSmall')]: {
        gap: 4,
        padding: '20px 12px 22px',
      },
    }),
    // Selected day: outline only, so it reads as weaker than a filled, selected time.
    dayTileSelected: css({
      borderColor: theme.mvf.palette.component.answerSelectedColor,
      boxShadow: `inset 0 0 0 1px ${theme.mvf.palette.component.answerSelectedColor}`,
      '& [data-tile-part="num"]': {
        color: theme.mvf.palette.component.answerSelectedColor,
      },
    }),
    dayTileWeekday: css({
      fontSize: 11,
      fontWeight: theme.mvf.typography.weight.thick,
      textTransform: 'uppercase',
      letterSpacing: '0.04em',
      color: mutedText,
      [theme.breakpoints.up('tabletSmall')]: { fontSize: 13 },
    }),
    dayTileNum: css({
      fontSize: 22,
      fontWeight: 800,
      [theme.breakpoints.up('tabletSmall')]: { fontSize: 30 },
    }),
    dayTileMonth: css({
      fontSize: 11,
      color: mutedText,
      [theme.breakpoints.up('tabletSmall')]: { fontSize: 15 },
    }),
    dayTileRow: css({
      display: 'flex',
      flexWrap: 'wrap',
      gap: ITEM_GAP,
      justifyContent: 'flex-start',
    }),
    pagerTop: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 8,
      marginBottom: 12,
    }),
    pagerRange: css({
      '& p': {
        fontWeight: theme.mvf.typography.weight.thick,
        color: theme.mvf.palette.text.secondaryTextColor,
      },
    }),
    pagerArrows: css({
      display: 'none',
      gap: 6,
      [theme.breakpoints.up('tabletSmall')]: { display: 'flex' },
    }),
    pagerArrow: css({
      minWidth: 0,
      width: 30,
      height: 30,
      borderRadius: '50%',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      color: theme.mvf.palette.text.primaryTextColor,
      lineHeight: 1,
      '&.Mui-disabled': { opacity: 0.35 },
    }),
    // Horizontal pages of day tiles. Scroll-snap gives free swipe on touch; the
    // scrollbar is hidden, and the right edge fades so the peeking next card
    // reads as "scroll for more".
    pagerTrack: css({
      display: 'flex',
      overflowX: 'auto',
      scrollSnapType: 'x mandatory',
      scrollBehavior: 'smooth',
      scrollbarWidth: 'none',
      msOverflowStyle: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    }),
    // A page is narrower than the track so the next page's first card peeks in.
    // The right padding equals the tile gap, so that peek sits the same distance
    // from the last tile as the tiles sit from each other.
    pagerPage: css({
      flex: '0 0 88%',
      minWidth: 0,
      scrollSnapAlign: 'start',
      display: 'flex',
      gap: ITEM_GAP,
      paddingRight: ITEM_GAP,
      justifyContent: 'flex-start',
    }),
    // Fades the side where a tile is cut off, set from measured overflow.
    pagerTrackFadeLeft: css({
      WebkitMaskImage:
        'linear-gradient(to right, transparent 0, #000 18px, #000 100%)',
      maskImage:
        'linear-gradient(to right, transparent 0, #000 18px, #000 100%)',
    }),
    pagerTrackFadeRight: css({
      WebkitMaskImage:
        'linear-gradient(to right, #000 calc(100% - 18px), transparent 100%)',
      maskImage:
        'linear-gradient(to right, #000 calc(100% - 18px), transparent 100%)',
    }),
    pagerTrackFadeBoth: css({
      WebkitMaskImage:
        'linear-gradient(to right, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%)',
      maskImage:
        'linear-gradient(to right, transparent 0, #000 18px, #000 calc(100% - 18px), transparent 100%)',
    }),
    pagerDots: css({
      display: 'flex',
      gap: 6,
      justifyContent: 'center',
      marginTop: 10,
      [theme.breakpoints.up('tabletSmall')]: { display: 'none' },
    }),
    pagerDot: css({
      width: 6,
      height: 6,
      borderRadius: '50%',
      backgroundColor: theme.mvf.palette.component.answerUnselectedColor,
    }),
    pagerDotOn: css({
      backgroundColor: theme.mvf.palette.component.answerSelectedColor,
    }),
    slotUnavailable: css({
      textTransform: 'none',
      borderRadius: '20px',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      padding: '6px 16px',
      textDecoration: 'line-through',
      color: theme.mvf.palette.text.secondaryTextColor,
    }),
    nearbyCard: css({
      backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      borderRadius: '6px',
      padding: 15,
      marginBottom: 10,
    }),
    // The chosen clinic (the one open) stands out with an accent border; an inset
    // shadow gives the 2px weight without shifting the other cards.
    nearbyCardOpen: css({
      borderColor: theme.mvf.palette.component.answerSelectedColor,
      boxShadow: `inset 0 0 0 1px ${theme.mvf.palette.component.answerSelectedColor}`,
    }),
    // A clinic that is not open reads fainter and opens on click.
    nearbyCardClosed: css({
      opacity: 0.75,
      cursor: 'pointer',
      '&:hover': { opacity: 1 },
    }),
    nearbyHeader: css({
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 10,
    }),
    distanceBadge: css({
      backgroundColor: alpha(
        theme.mvf.palette.component.answerSelectedColor,
        0.15
      ),
      borderRadius: '12px',
      padding: '2px 10px',
      flexShrink: 0,
      '& p': {
        color: theme.mvf.palette.component.answerSelectedColor,
        fontWeight: theme.mvf.typography.weight.thick,
      },
    }),
    // On a clinic that is not open the badge is neutral, so the accent badge
    // marks the chosen clinic alone.
    distanceBadgeMuted: css({
      backgroundColor: 'transparent',
      border: 'solid',
      borderWidth: theme.mvf.border.width.thin,
      borderColor: theme.mvf.palette.component.answerUnselectedColor,
      '& p': { color: theme.mvf.palette.text.secondaryTextColor },
    }),
    nearbyAddress: css({ textAlign: 'left', '& p': { textAlign: 'left' } }),
    addressPin: css({
      width: 12,
      height: 12,
      marginRight: 4,
      verticalAlign: 'middle',
    }),
    expandable: css({
      display: 'grid',
      gridTemplateRows: '0fr',
      transition: 'grid-template-rows 350ms ease-in-out',
      // The collapsed grid keeps its day/time buttons in the DOM for a smooth
      // reopen, but visibility:hidden takes them out of the tab order so the
      // keyboard does not land on controls the user cannot see.
      visibility: 'hidden',
    }),
    expandableOpen: css({ gridTemplateRows: '1fr', visibility: 'visible' }),
    expandableInner: css({ overflow: 'hidden', minHeight: 0 }),
    toggleLink: css({
      textTransform: 'none',
      padding: 0,
      marginTop: 8,
      color: theme.mvf.palette.component.answerSelectedColor,
      fontWeight: theme.mvf.typography.weight.thick,
    }),
    // A clinic's view-all link is muted, so the accent is reserved for the
    // chosen clinic and the selected day/time.
    toggleLinkMuted: css({
      color: mutedText,
    }),
    solarBackLink: css({
      color: theme.mvf.palette.component.continueButtonColor,
      fontWeight: theme.mvf.typography.weight.medium,
    }),
    toggleLinkContainer: css({ textAlign: 'center' }),
    // Pinned footer: the Continue button stays visible while the cards scroll.
    continueContainer: css({
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      marginTop: 8,
      paddingTop: 12,
      paddingBottom: 4,
      textAlign: 'center',
      backgroundColor: theme.mvf.palette.background.widgetBackgroundColor,
    }),
    message: css({ margin: '10px 0', textAlign: 'center' }),
    nearbySlots: css({ marginTop: 10 }),
    legalLine: css({
      // 2rem top margin gives padding below the final review card.
      margin: '2rem 0 12px 0',
      // Matches the pre-submission legal statement (makeLegalStatementStyling.js):
      // the same grey and the same 11px -> 12px sizing, so consent copy reads the
      // same before and after submission.
      '& p': {
        color: '#808080',
        fontSize: `${theme.mvf.fontSize.originalTiny}px`,
        [theme.breakpoints.up('tabletSmall')]: {
          fontSize: `${theme.mvf.fontSize.originalLittle}px`,
        },
      },
    }),
  };
};

export default makeSlotPickerStyles;
